/**
 * EmailSendRulesCard - system_admin control for outbound email mode.
 *
 * Three radio options drive the backend gate inside
 * ``email_outbound_service.send_email``:
 *
 *   - production: send normally.
 *   - suppression: drop every outbound send silently.
 *   - bypass: redirect every outbound send to a single operator
 *     address.
 *
 * The bypass-recipient input is only meaningful when mode=bypass; it's
 * disabled (but still visible so the agent doesn't have to wonder
 * where it went) in the other two modes.
 *
 * On save, the AppShell banner's query is invalidated so the change
 * is visible to the saving admin in their own tab without waiting
 * for the 60s refetch tick.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";

import { Alert, Button, Card, Input } from "@/components/ui";
import { EMAIL_SEND_MODE_QUERY_KEY } from "@/hooks/useEmailSendMode";
import api from "@/lib/api";
import type { EmailSendMode, EmailSendRulesPayload } from "@/types/api";

const QUERY_KEY = ["admin", "email-send-rules"] as const;

const OPTIONS: Array<{
  value: EmailSendMode;
  label: string;
  description: string;
}> = [
  {
    value: "production",
    label: "Production",
    description: "Emails are sent normally.",
  },
  {
    value: "suppression",
    label: "Suppression",
    description: "No emails are sent. The system behaves normally otherwise.",
  },
  {
    value: "bypass",
    label: "Bypass",
    description:
      "All outbound emails are redirected to the bypass recipient instead of the real recipient.",
  },
];

export function EmailSendRulesCard(): JSX.Element {
  const queryClient = useQueryClient();

  // The current saved value from the server. This drives the
  // "Saved" / "Unsaved changes" indicator.
  const query = useQuery<EmailSendRulesPayload>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const r = await api.get<EmailSendRulesPayload>("/admin/email-send-rules");
      return r.data;
    },
  });

  // Local form state. Seeded from the server data on first load and
  // whenever the server data changes (e.g. another admin saved in a
  // different tab and we just refetched).
  const [mode, setMode] = useState<EmailSendMode>("production");
  const [bypassRecipient, setBypassRecipient] = useState<string>("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [savedRecently, setSavedRecently] = useState<boolean>(false);

  // Seed local form state ONLY when the server data is structurally
  // different from what we last seeded. Without this guard, every
  // TanStack Query re-render reseeds the inputs and clobbers any
  // edits the admin is in the middle of typing (an external refetch
  // wouldn't even need to land - just hovering an option that
  // re-renders the parent is enough to lose the in-progress change).
  const lastSeededRef = useRef<string>("");
  useEffect(() => {
    if (!query.data) return;
    const sig = `${query.data.mode}|${query.data.bypass_recipient}`;
    if (sig === lastSeededRef.current) return;
    lastSeededRef.current = sig;
    setMode(query.data.mode);
    setBypassRecipient(query.data.bypass_recipient);
  }, [query.data]);

  const saver = useMutation({
    mutationFn: async (body: { mode: EmailSendMode; bypass_recipient: string }) => {
      const r = await api.put<EmailSendRulesPayload>(
        "/admin/email-send-rules",
        body,
      );
      return r.data;
    },
    onSuccess: (saved) => {
      // Update the admin-side cache so the form reflects what was
      // actually saved (the server may have normalized values).
      queryClient.setQueryData(QUERY_KEY, saved);
      // Invalidate the banner so it picks up the new mode in this
      // same tab without waiting for the 60s refetch tick.
      void queryClient.invalidateQueries({
        queryKey: EMAIL_SEND_MODE_QUERY_KEY,
      });
      setSavedRecently(true);
      window.setTimeout(() => setSavedRecently(false), 1500);
    },
  });

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);
    if (mode === "bypass" && !bypassRecipient.trim()) {
      setValidationError(
        "A bypass recipient is required when bypass mode is selected.",
      );
      return;
    }
    saver.mutate({ mode, bypass_recipient: bypassRecipient.trim() });
  }

  const dirty =
    !!query.data &&
    (mode !== query.data.mode ||
      bypassRecipient !== query.data.bypass_recipient);

  return (
    <Card padding="md" data-testid="email-send-rules-card">
      <h3 className="text-base font-semibold text-neutral-900">
        Email Send Rules
      </h3>
      <p className="mt-1 text-sm text-neutral-600">
        Controls outbound email sending. Inbound email is unaffected in
        all modes - the system always receives + processes incoming
        messages normally.
      </p>

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <fieldset className="space-y-2" disabled={query.isLoading}>
          <legend className="sr-only">Mode</legend>
          {OPTIONS.map((opt) => {
            const radioId = `email-send-mode-${opt.value}`;
            return (
              <label
                key={opt.value}
                htmlFor={radioId}
                className="flex cursor-pointer items-start gap-3 rounded-md border border-neutral-200 p-3 hover:bg-neutral-50"
                data-testid={`email-send-rules-option-${opt.value}`}
              >
                <input
                  id={radioId}
                  type="radio"
                  name="email-send-mode"
                  value={opt.value}
                  checked={mode === opt.value}
                  onChange={() => setMode(opt.value)}
                  className="mt-1"
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-neutral-900">
                    {opt.label}
                  </div>
                  <div className="text-xs text-neutral-600">
                    {opt.description}
                  </div>
                </div>
              </label>
            );
          })}
        </fieldset>

        {mode === "bypass" && (
          <Input
            id="bypass-recipient"
            type="email"
            label="Bypass recipient"
            helperText="All outbound emails are redirected to this address."
            value={bypassRecipient}
            onChange={(e) => setBypassRecipient(e.target.value)}
            required
            data-testid="email-send-rules-bypass-input"
          />
        )}

        {validationError && (
          <Alert variant="error">{validationError}</Alert>
        )}
        {saver.isError && (
          <Alert variant="error">
            Save failed: {(saver.error as Error)?.message ?? "unknown error"}
          </Alert>
        )}

        <div className="flex items-center gap-3">
          {/* ``data-just-saved`` on the wrapper triggers the same
              emerald-ring keyframe pulse that the Properties-card
              dropdowns use after a successful PATCH. The keyframe
              applies to the inner ``select`` / ``input`` /
              ``[role=combobox]`` elements via the index.css selector
              but also to the Button by virtue of being inside this
              span when ``savedRecently`` is true. The styled outline
              draws the admin's eye to the just-saved control even
              when their attention was elsewhere mid-save. */}
          <span
            data-just-saved={savedRecently ? "true" : "false"}
            className="inline-block"
          >
            <Button
              type="submit"
              variant="primary"
              disabled={!dirty || saver.isPending}
              isLoading={saver.isPending}
              data-testid="email-send-rules-save"
            >
              Save
            </Button>
          </span>
          {savedRecently && (
            <span
              className="text-sm text-emerald-700"
              data-testid="email-send-rules-saved"
            >
              Saved.
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

export default EmailSendRulesCard;
